import { useState, useEffect } from 'react';
import { Campaign, CreateCampaignRequest, UpdateCampaignRequest } from '../../../shared/types/campaign';

export const useCampaigns = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/campaigns`);
      if (!response.ok) {
        throw new Error(`Failed to fetch campaigns: ${response.statusText}`);
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data : (data?.campaigns || []);
      setCampaigns(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch campaigns');
      console.error('Error fetching campaigns:', err);
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async (campaignData: CreateCampaignRequest): Promise<Campaign | null> => {
    try {
      const response = await fetch(`/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(campaignData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create campaign: ${response.statusText}`);
      }

      const newCampaign = await response.json();
      setCampaigns(prev => [...prev, newCampaign]);
      return newCampaign;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
      console.error('Error creating campaign:', err);
      return null;
    }
  };

  const updateCampaign = async (campaignData: UpdateCampaignRequest): Promise<Campaign | null> => {
    try {
      const response = await fetch(`/api/campaigns/${campaignData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(campaignData),
      });

      if (!response.ok) {
        throw new Error(`Failed to update campaign: ${response.statusText}`);
      }

      const updatedCampaign = await response.json();
      setCampaigns(prev => 
        prev.map(campaign => 
          campaign.id === campaignData.id ? updatedCampaign : campaign
        )
      );
      return updatedCampaign;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update campaign');
      console.error('Error updating campaign:', err);
      return null;
    }
  };

  const deleteCampaign = async (campaign: Campaign): Promise<boolean> => {
    try {
      const response = await fetch(`/api/campaigns`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(campaign),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete campaign: ${response.statusText}`);
      }

      setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete campaign');
      console.error('Error deleting campaign:', err);
      return false;
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  return {
    campaigns,
    loading,
    error,
    fetchCampaigns,
    createCampaign,
    updateCampaign,
    deleteCampaign,
  };
};
